import { useState, useEffect, type ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import type { FaqItem, CreateFaqPayload, UpdateFaqPayload } from '../../services/cms.service';

interface FaqFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  faq?: FaqItem;
  onClose: () => void;
  onSubmit: (payload: CreateFaqPayload | UpdateFaqPayload) => Promise<void>;
}

export default function FaqFormDialog({
  open,
  mode,
  faq,
  onClose,
  onSubmit,
}: Readonly<FaqFormDialogProps>) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setQuestion(faq?.question ?? '');
      setAnswer(faq?.answer ?? '');
      setError('');
    }
  }, [open, faq]);

  const handleSubmit = async () => {
    if (!question.trim()) {
      setError('Question is required');
      return;
    }
    if (!answer.trim()) {
      setError('Answer is required');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        question: question.trim(),
        answer: answer.trim(),
      });
      onClose();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } }; message?: string };
      setError(axiosError.response?.data?.message ?? axiosError.message ?? 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  let submitButtonContent: ReactNode;
  if (submitting) {
    submitButtonContent = <CircularProgress size={20} />;
  } else if (mode === 'create') {
    submitButtonContent = 'Add';
  } else {
    submitButtonContent = 'Save';
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Add FAQ' : 'Edit FAQ'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            fullWidth
            required
            multiline
            rows={2}
          />
          <TextField
            label="Answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitButtonContent}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
